import React, { useEffect } from 'react';
import { Box, Container, Typography, Grid, Paper, Button, List, ListItem, ListItemIcon, ListItemText } from '@mui/material';
import { keyframes } from '@mui/system';
import { Link } from 'react-router-dom';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';

// Import images
import marketingImg from '../images/Property Marketing.png';
import backgroundHero from '../images/BackgroundHeroSection.jpg';

const fadeInUp = keyframes`
  from { opacity: 0; transform: translateY(30px); }
  to   { opacity: 1; transform: translateY(0); }
`;

const PropertyMarketing = () => {

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const features = [
    "AI-driven strategic advertising",
    "Immersive virtual tours",
    "Tailored digital marketing campaigns",
    "Interactive brand management",
    "Smart lead conversion tools",
    "Real-time campaign analytics"
  ];

  return (
    <Box sx={{ backgroundColor: '#fbfbfb', minHeight: '100vh' }}>
      {/* Hero Section */}
      <Box
        sx={{
          position: 'relative',
          height: { xs: '350px', md: '500px' },
          backgroundImage: `linear-gradient(rgba(10, 35, 59, 0.8), rgba(10, 35, 59, 0.8)), url(${backgroundHero})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          display: 'flex',
          alignItems: 'center',
          color: 'white',
          overflow: 'hidden'
        }}
      >
        <Container maxWidth="lg">
          <Box sx={{ maxWidth: '800px', animation: `${fadeInUp} 1s ease-out` }}>
            <Typography
              variant="h2"
              component="h1"
              sx={{
                fontWeight: 700,
                fontSize: { xs: '2.5rem', md: '4rem' },
                mb: 2,
                letterSpacing: '-0.02em'
              }}
            >
              Property Marketing
            </Typography>
            <Box sx={{ width: 80, height: 4, backgroundColor: '#f66b10', borderRadius: 2, mb: 4 }} />
            <Typography
              variant="h5"
              sx={{
                fontWeight: 300,
                lineHeight: 1.6,
                opacity: 0.9,
                fontSize: { xs: '1.1rem', md: '1.5rem' }
              }}
            >
              Revolutionize your property's visibility with our futuristic marketing solutions.
            </Typography>
          </Box>
        </Container>
      </Box>

      {/* Main Content Section */}
      <Container maxWidth="lg" sx={{ py: { xs: 8, md: 12 } }}>
        <Grid container spacing={8} alignItems="center">
          <Grid item xs={12} md={6}>
            <Box sx={{ animation: `${fadeInUp} 1s ease-out 0.2s both` }}>
              <Typography variant="h4" sx={{ color: '#0a233b', fontWeight: 600, mb: 3 }}>
                Captivate & Convert
              </Typography>
              <Typography variant="body1" sx={{ color: '#6a6565', mb: 4, lineHeight: 1.8, fontSize: '1.1rem' }}>
                In a crowded market, standing out is essential. We harness AI-driven strategies, immersive virtual tours, and targeted campaigns to captivate and convert your audience.
              </Typography>
              <Typography variant="body1" sx={{ color: '#6a6565', mb: 4, lineHeight: 1.8, fontSize: '1.1rem' }}>
                From high-end photography to data-backed ad placements, we ensure your property gets the attention it deserves from the right people.
              </Typography>

              <List sx={{ mb: 4 }}>
                <Grid container spacing={1}>
                  {features.map((feature, index) => (
                    <Grid item xs={12} sm={6} key={index}>
                      <ListItem disablePadding sx={{ py: 1 }}>
                        <ListItemIcon sx={{ minWidth: 36 }}>
                          <CheckCircleIcon sx={{ color: '#f66b10' }} />
                        </ListItemIcon>
                        <ListItemText
                          primary={feature}
                          primaryTypographyProps={{
                            variant: 'body2',
                            fontWeight: 500,
                            color: '#0a233b'
                          }}
                        />
                      </ListItem>
                    </Grid>
                  ))}
                </Grid>
              </List>

              <Button
                component={Link}
                to="/contact"
                variant="contained"
                size="large"
                endIcon={<ArrowForwardIcon />}
                sx={{
                  backgroundColor: '#f66b10',
                  color: 'white',
                  px: 4,
                  py: 1.5,
                  borderRadius: '30px',
                  textTransform: 'none',
                  fontSize: '1rem',
                  fontWeight: 600,
                  '&:hover': {
                    backgroundColor: '#d65600',
                  }
                }}
              >
                Boost Your Visibility
              </Button>
            </Box>
          </Grid>
          <Grid item xs={12} md={6}>
            <Box
              sx={{
                position: 'relative',
                animation: `${fadeInUp} 1s ease-out 0.4s both`,
                '&:after': {
                  content: '""',
                  position: 'absolute',
                  top: -20,
                  right: -20,
                  width: '100%',
                  height: '100%',
                  backgroundColor: '#a6d1ea',
                  opacity: 0.2,
                  borderRadius: 4,
                  zIndex: -1
                }
              }}
            >
              <Box
                component="img"
                src={marketingImg}
                alt="Property Marketing"
                sx={{
                  width: '100%',
                  height: 'auto',
                  borderRadius: 4,
                  boxShadow: '0 20px 40px rgba(0,0,0,0.1)',
                  backgroundColor: 'white',
                  p: 4,
                  objectFit: 'contain'
                }}
              />
            </Box>
          </Grid>
        </Grid>
      </Container>

      {/* Why Lantena Section */}
      <Box sx={{ backgroundColor: '#0a233b', color: 'white', py: { xs: 8, md: 12 } }}>
        <Container maxWidth="lg">
          <Typography variant="h3" align="center" sx={{ fontWeight: 600, mb: 8, fontSize: { xs: '2rem', md: '3rem' } }}>
            Why Our Marketing Works
          </Typography>
          <Grid container spacing={4}>
            {[
              {
                title: "Reach",
                desc: "We target the right demographic across multiple digital platforms."
              },
              {
                title: "Engagement",
                desc: "High-quality content that keeps potential tenants or buyers interested."
              },
              {
                title: "Results",
                desc: "Data-driven adjustments to maximize conversion rates and ROI."
              }
            ].map((item, index) => (
              <Grid item xs={12} md={4} key={index}>
                <Paper
                  elevation={0}
                  sx={{
                    p: 4,
                    height: '100%',
                    backgroundColor: 'rgba(255,255,255,0.05)',
                    border: '1px solid rgba(255,255,255,0.1)',
                    borderRadius: 3,
                    transition: 'transform 0.3s',
                    '&:hover': {
                      transform: 'translateY(-10px)',
                      backgroundColor: 'rgba(255,255,255,0.08)',
                    }
                  }}
                >
                  <Typography variant="h5" sx={{ color: '#f66b10', fontWeight: 600, mb: 2 }}>
                    {item.title}
                  </Typography>
                  <Typography variant="body1" sx={{ color: 'rgba(255,255,255,0.7)', lineHeight: 1.7 }}>
                    {item.desc}
                  </Typography>
                </Paper>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* CTA Section */}
      <Container maxWidth="md" sx={{ py: { xs: 10, md: 15 }, textAlign: 'center' }}>
        <Typography variant="h4" sx={{ color: '#0a233b', fontWeight: 600, mb: 3 }}>
          Ready to Get Noticed?
        </Typography>
        <Typography variant="body1" sx={{ color: '#6a6565', mb: 5, fontSize: '1.1rem' }}>
          Join the many property owners who have achieved their goals with Lantena's marketing expertise. Our team is ready to promote your property.
        </Typography>
        <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', flexWrap: 'wrap' }}>
          <Button
            component={Link}
            to="/contact"
            variant="contained"
            size="large"
            sx={{
              backgroundColor: '#f66b10',
              color: 'white',
              px: { xs: 4, md: 6 },
              py: 2,
              borderRadius: '30px',
              textTransform: 'none',
              fontWeight: 600,
              '&:hover': { backgroundColor: '#d65600' }
            }}
          >
            Contact Us Now
          </Button>
          <Button
            component={Link}
            to="/about-us"
            variant="outlined"
            size="large"
            sx={{
              borderColor: '#0a233b',
              color: '#0a233b',
              px: { xs: 4, md: 6 },
              py: 2,
              borderRadius: '30px',
              textTransform: 'none',
              fontWeight: 600,
              '&:hover': {
                borderColor: '#0a233b',
                backgroundColor: 'rgba(10, 35, 59, 0.05)'
              }
            }}
          >
            Learn About Lantena
          </Button>
        </Box>
      </Container>
    </Box>
  );
};

export default PropertyMarketing;
